<script>
import { HamletTable } from '@core/components/common'
import { SupplyChain } from '@core/api'
import dateRenderer from '@core/mixins/date-renderer'
import NoteType from '@/views/supply-chain/supplier/mixins/NoteType'

const DEFAULT_PER_PAGE = 30

function normalizeSort(sort) {
    if (!sort || typeof sort !== 'object' || Array.isArray(sort)) {
        return null
    }

    if (!sort.by || !['asc', 'desc'].includes(sort.direction)) {
        return null
    }

    return {
        by: sort.by,
        direction: sort.direction,
    }
}

function cloneObject(value) {
    return JSON.parse(JSON.stringify(value))
}

function buildSettings(filters, perPage, page, sort) {
    const settings = {
        filters,
        perPage,
        page,
    }

    if (sort) {
        settings.sort = sort
    }

    return settings
}

function normalizeSettings(settings) {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {
            filters: {},
            perPage: DEFAULT_PER_PAGE,
            page: 1,
            sort: null,
        }
    }

    return {
        filters: cloneObject(settings.filters || {}),
        perPage: Number(settings.perPage) || DEFAULT_PER_PAGE,
        page: Number(settings.page) || 1,
        sort: normalizeSort(settings.sort),
    }
}

export default {
    components: {
        HamletTable,
    },
    mixins: [dateRenderer, NoteType],
    data() {
        const savedSettings = normalizeSettings(this.$store.getters.runtimeConfig.followUpsSettings)

        return {
            savedFilters: savedSettings.filters,
            savedSort: savedSettings.sort,
            savedPerPage: savedSettings.perPage,
            savedPage: savedSettings.page,
            fields: [
                {
                    key: 'supplier',
                    label: this.$t('Component(Grid):column-supplier'),
                    title: this.$t('Component(Grid):column-supplier'),
                    order: 0,
                    active: true,
                    sortable: true,
                    sortKey: 'supplierName',
                },
                {
                    key: 'country',
                    label: this.$t('Component(Grid):column-country'),
                    title: this.$t('Component(Grid):column-country'),
                    order: 0,
                    active: true,
                },
                {
                    key: 'numberOfSentRequests',
                    label: this.$t('Component(Grid):column-number-of-sent-requests'),
                    title: this.$t('Component(Grid):column-number-of-sent-requests'),
                    order: 0,
                    active: true,
                },
                {
                    key: 'note',
                    label: this.$t('Component(Grid):column-follow-up-note'),
                    title: this.$t('Component(Grid):column-follow-up-note'),
                    order: 0,
                    active: true,
                    tdClass: 'wrap-spaces',
                },

                {
                    key: 'type',
                    label: this.$t('Component(Grid):column-type'),
                    title: this.$t('Component(Grid):column-type'),
                    order: 0,
                    active: true,
                },

                {
                    key: 'dateTime',
                    label: this.$t('Component(Label):date'),
                    title: this.$t('Component(Label):date'),
                    order: 0,
                    active: true,
                    sortable: true,
                    sortKey: 'date',
                    defaultSort: 'desc',
                },
            ],

            config: null,
            fetchCallback: null,
        }
    },

    methods: {
        saveSettings() {
            this.$store.dispatch('setRuntimeProperty', {
                name: 'followUpsSettings',
                value: buildSettings(
                    this.config ? this.config.filters : {},
                    this.savedPerPage,
                    this.savedPage,
                    this.config ? this.config.sort : null,
                ),
                removeOnOrganizationSwitch: true,
            })
        },
        onTableCreated(config, cb) {
            this.config = config
            this.fetchCallback = cb
        },
        onPerPageChange(perPage) {
            this.savedPerPage = perPage
            this.saveSettings()
        },
        onPageChange(page) {
            this.savedPage = page
            this.saveSettings()
        },
        onRowClick(followUp) {
            this.$router.push({
                name: 'supplier-follow-up',
                params: { id: followUp.id },
            })
        },
        fetch(config, cb) {
            this.config = config
            this.fetchCallback = cb
            this.savedPage = config.page
            this.savedPerPage = config.perPage
            this.saveSettings()

            return SupplyChain
                .getFollowUps(config.filters, config.sort, config.page, config.perPage)
                .then(r => {
                    cb(r.data.totalItems, r.data.items)
                })
        },
    },
}
</script>

<template>
    <div>
        <HamletTable
            ref="hamletTable"
            :columns="fields"
            :fetch="fetch"
            :sorting="true"
            :default-filters="savedFilters"
            :default-sort="savedSort"
            :default-per-page="savedPerPage"
            :default-page="savedPage"
            @created="onTableCreated"
            @onRowClick="onRowClick"
            @per-page-change="onPerPageChange"
            @page-change="onPageChange"
        >
            <template #header-label>
                {{ $t('Page(SentRequest):Component(Label):follow-up-label') }}
            </template>

            <template #cell-note="data">
                {{ data.item.note.note }}
            </template>

            <template #cell-country="data">
                {{ data.item.supplier && data.item.supplier.state ? data.item.supplier.state.title : '' }}
            </template>

            <template #cell-type="data">
                {{ data.item.note.type ? resolveType(data.item.note) : '' }}
            </template>

            <template #cell-dateTime="data">
                {{ data.item.note.createdAt ? renderDateTime(data.item.note.createdAt) : '' }}
            </template>
        </HamletTable>
    </div>
</template>
